import { useEffect, type RefObject } from 'react';

let openCount = 0;

function scrollsBy(el: HTMLElement, axis: 'x' | 'y', delta: number): boolean {
  const style = getComputedStyle(el);
  const overflow = axis === 'y' ? style.overflowY : style.overflowX;
  if (overflow !== 'auto' && overflow !== 'scroll') return false;

  if (axis === 'y') {
    if (el.scrollHeight <= el.clientHeight) return false;
    // Палец вниз — содержимое едет к началу
    return delta > 0
      ? el.scrollTop > 0
      : el.scrollTop + el.clientHeight < el.scrollHeight - 1;
  }

  if (el.scrollWidth <= el.clientWidth) return false;
  return delta > 0
    ? el.scrollLeft > 0
    : el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
}

/**
 * Модальный слой поверх приложения: фокус и клавиатура уходят с фона,
 * фон становится inert, а жест, который некому прокрутить внутри слоя,
 * гасится — иначе iOS отдаёт его странице под шторкой.
 */
export function useModalLock(
  active: boolean,
  layerRef: RefObject<HTMLElement>,
  focusRef: RefObject<HTMLElement>
) {
  useEffect(() => {
    if (!active) return;

    const previous = document.activeElement as HTMLElement | null;
    previous?.blur();
    focusRef.current?.focus({ preventScroll: true });

    const root = document.getElementById('root');
    openCount += 1;
    root?.setAttribute('inert', '');

    return () => {
      openCount -= 1;
      if (openCount === 0) root?.removeAttribute('inert');

      // В поле не возвращаем: на телефоне это снова открыло бы клавиатуру
      if (previous?.isConnected && !previous.matches('input, textarea')) {
        previous.focus({ preventScroll: true });
      }
    };
  }, [active, focusRef]);

  useEffect(() => {
    const layer = layerRef.current;
    if (!active || !layer) return;

    let startX = 0;
    let startY = 0;

    const onStart = (e: TouchEvent) => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
    };

    const onMove = (e: TouchEvent) => {
      const dx = e.touches[0].clientX - startX;
      const dy = e.touches[0].clientY - startY;
      const axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
      const delta = axis === 'x' ? dx : dy;

      for (
        let el = e.target as HTMLElement | null;
        el && el !== layer;
        el = el.parentElement
      ) {
        if (scrollsBy(el, axis, delta)) return;
      }

      if (e.cancelable) e.preventDefault();
    };

    layer.addEventListener('touchstart', onStart, { passive: true });
    layer.addEventListener('touchmove', onMove, { passive: false });

    return () => {
      layer.removeEventListener('touchstart', onStart);
      layer.removeEventListener('touchmove', onMove);
    };
  }, [active, layerRef]);
}
